import axios from 'axios';
import { computed, ComputedRef, reactive } from "vue";

import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter";

import HourlyPopularityStats from '../DTOs/HourlyPopularityStats';
import { FetchForAliasArgs, FetchForTagArgs, FetchForTeamArgs } from './types';

export type PopularityStatsData = { dataset: number[] };

export type iPopularityStatsAPI = {
    dataset: ComputedRef<Readonly<HourlyPopularityStats>>,
    loading: ComputedRef<boolean>,
    setRouter: (router: HttpRouter<'get'>) => iPopularityStatsAPI,
    fetchForTeam: (args: FetchForTeamArgs) => Promise<void>
    fetchForAlias: (args: FetchForAliasArgs) => Promise<void>
    fetchForTag: (args: FetchForTagArgs) => Promise<void>
}

export function DefaultPopularitystats(numItems: number = 24): number[] {
    let retval: number[] = [];
    for (let i = 0; i < numItems; i++) {
        retval.push(i * 11);
    }

    return retval;
}

/**
 * A tool to allow you to fetch and interact with "popular times" analytics
 * data fetched from the server.
 */
export default function HourlyPopularityStatsAPI(debug = false): iPopularityStatsAPI {
    const state = reactive<{
        dataset: HourlyPopularityStats,
        loading: boolean
    }>({
        dataset: new HourlyPopularityStats(),
        loading: false,
    });

    let router: HttpRouter<'get'> = axios;

    async function _fetch(url: string, from: string, to: string): Promise<void> {
        state.loading = true;

        if (debug) {
            state.dataset = new HourlyPopularityStats(
                DefaultPopularitystats(24),
                from,
                to
            );
        } else {
            const response: HttpResponse<PopularityStatsData> = await router.get(url);

            if (response.status !== 200) {
                throw new Error('Could not fetch hourly popularity stats');
            }

            state.dataset = new HourlyPopularityStats(response.data.dataset, from, to);
        }

        state.loading = false;
    }

    return {
        loading: computed(() => state.loading),
        dataset: computed(() => state.dataset),

        fetchForTeam: async function ({
            orgID,
            teamID = null,
            from,
            to,
            archived = false
        }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/popularity/hour?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url, from, to);
        },

        fetchForAlias: async function ({
            orgID,
            teamID = null,
            alias,
            from,
            to,
            archived = false
        }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/popularity/hour?alias[]=${encodeURIComponent(alias)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url, from, to);
        },

        fetchForTag: async function ({
            orgID,
            teamID = null,
            tag,
            from,
            to,
            archived = false
        }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/popularity/hour?tag[]=${encodeURIComponent(tag)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url, from, to);
        },

        /**
         * Allows you to swap out the router for a fake one when writing tests
         */
        setRouter(newRouter) {
            router = newRouter;
            return this;
        }
    };
}
